import React, { useEffect, useState } from 'react';
import { Modal, Backdrop, Fade, Box, Typography, Button, Divider, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Dropdown, TextInput } from 'qubCommon/Inputs';
import { useFormContext } from 'react-hook-form';
import { checkDiscountCode } from 'apis/controllers/membership';
import { useSnackbar } from 'notistack';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

const DiscountChip = styled(Chip)(({ theme }) => ({
    backgroundColor: '#ff3d00',
    color: 'white',
    fontWeight: 'bold',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '14px'
}));

const DiscountTag = ({ discount }) => {
    return <DiscountChip label={`-${discount}%`} />;
};

const MembershipModal = ({
    open,
    onClose,
    onSubmit,
    products = [],
    membershipAmount,
    membershipType,
    leadUserId,
    membershipDiscount,
    discountCodeDefault = null,
    discountCommentsDefault = null
}) => {
    const { t } = useTranslation();
    const form = useFormContext();
    const [selectedAmount, setSelectedAmount] = useState(membershipAmount);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [discountFieldsOn, setDiscountFieldsOn] = useState(!!discountCodeDefault);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [discount, setDiscount] = useState(membershipDiscount ?? 0);
    const [discountCode, setDiscountCode] = useState(discountCodeDefault);
    const [discountComments, setDiscountComments] = useState(discountCommentsDefault);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (discountCode && open) {
            setDiscountFieldsOn(true);
            form.setValue('discountCode', discountCode);
            form.setValue('discountComments', discountComments);
        }
    }, [discountCommentsDefault, discountCodeDefault, open]);

    useEffect(() => {
        if (membershipAmount) setSelectedAmount(membershipAmount);
    }, [membershipAmount]);

    useEffect(() => {
        if (membershipDiscount) setDiscount(membershipDiscount);
    }, [membershipDiscount]);

    const handleMembershipChange = (value) => {
        form.setValue('discountCode', null);
        form.setValue('discountComments', null);

        const selected = products.find((option) => form?.watch('membershipType') === option.id);
        setSelectedAmount(selected?.unitPrice);
        setSelectedLabel(selected?.label);
        setDiscount(0);
    };

    useEffect(() => {
        if (!open) {
            setDiscount(0);
            setDiscountFieldsOn(false);
            form.setValue('discountCode', null);
            form.setValue('discountComments', null);
            form.setValue('membershipType', form.getValues('membershipTypeSubmitted'));
        }
        if (open && membershipDiscount && form?.watch('membershipType') === membershipType) setDiscount(membershipDiscount);
    }, [open]);

    const handleSubmit = async () => {
        const selected = products.find((option) => form?.watch('membershipType') === option.id);
        setIsSubmitting(true);
        form.setValue('membershipTypeSubmitted', form?.watch('membershipType'));
        setDiscountComments(form.getValues('discountComments'));
        setDiscountCode(form.getValues('discountCode'));
        try {
            await onSubmit(
                form.watch('membershipType'),
                selectedAmount,
                selectedLabel ?? selected?.label,
                form?.watch('discountCode'),
                form?.watch('discountComments'),
                discount
            );
        } catch (e) {
            console.log(e);
            setDiscountComments(null);
            setDiscountCode(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApplyDiscount = async () => {
        setIsSubmitting(true);
        const selectedId = products.find((option) => form?.watch('membershipType') === option.id)?.id;

        const res = await checkDiscountCode({
            Code: form.getValues('discountCode'),
            PersonId: leadUserId,
            ProductServiceId: selectedId
        });
        if (res?.status === 'success') setDiscount(0.01 * res?.data?.discount);
        if (res?.status !== 'success') enqueueSnackbar(t(res?.message), { variant: res?.status });
        setIsSubmitting(false);
    };
    const handleRemoveDiscount = () => {
        form.setValue('discountCode', null);
        form.setValue('discountComments', null);
        setDiscount(0);
    };

    const handleDiscountFieldsChange = (e) => {
        setDiscountFieldsOn(e.target.checked);
        if (!e.target.checked) {
            form.setValue('discountCode', null);
            form.setValue('discountComments', null);
            setDiscount(0);
        }
    };

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
            <Fade in={open}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 3,
                        p: 4,
                        minWidth: 420,
                        maxWidth: '90%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" fontWeight="bold">
                            {t('RenewMembership')}
                        </Typography>
                        <IconButton onClick={onClose} sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)' }}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Divider />

                    {/* Membership Type Dropdown */}
                    <Dropdown
                        name="membershipType"
                        label={`${t('TypeOfMembership')}*`}
                        fullWidth
                        options={products.map((opt) => ({ label: t(opt.label), value: opt.id }))}
                        changeFn={(e) => handleMembershipChange(e)}
                        required
                    />

                    {/* New Discount Code & Comments Inputs */}
                    <FormControlLabel
                        control={<Checkbox />}
                        onChange={handleDiscountFieldsChange}
                        value={discountFieldsOn}
                        label={t('AddDiscount')}
                        checked={discountFieldsOn}
                    />
                    {discount > 0 && (
                        <Chip
                            label={`${discount * 100}% Discount Applied`}
                            color="success"
                            sx={{ fontWeight: 'bold', fontSize: '14px', mb: 2 }}
                        />
                    )}
                    {discountFieldsOn && (
                        <>
                            <TextInput
                                disabled={discount > 0}
                                name="discountCode"
                                label={t('Discount Code')}
                                fullWidth
                                defaultFromProps={false}
                            />
                            <TextInput
                                name="discountComments"
                                label={t('Discount Comments')}
                                fullWidth
                                multiline
                                rows={2}
                                defaultFromProps={false}
                            />
                        </>
                    )}
                    {/* Display Membership Cost */}
                    {form.watch('membershipType') ? (
                        <Typography sx={{ mt: 1, fontSize: 16, fontWeight: 'bold' }}>
                            {t('MembershipCost')}:{' '}
                            <span style={discount ? { textDecoration: 'line-through', color: 'darkred' } : { color: '#2E7D32' }}>
                                ${selectedAmount?.toFixed(2)} CAD
                            </span>
                            {discount ? (
                                <span style={{ marginLeft: 10, color: '#2E7D32' }}>
                                    ${(selectedAmount - selectedAmount * discount).toFixed(2)} CAD
                                </span>
                            ) : null}
                        </Typography>
                    ) : null}

                    {/* Submit Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        {discountFieldsOn && (
                            <Button
                                variant="contained"
                                sx={{ marginRight: 1 }}
                                color="primary"
                                onClick={discount > 0 ? handleRemoveDiscount : handleApplyDiscount}
                                disabled={!selectedAmount || isSubmitting}
                            >
                                {discount > 0 ? t('RemoveDiscount') : t('ApplyDiscount')}
                            </Button>
                        )}
                        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!selectedAmount || isSubmitting}>
                            {t('Submit')}
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default MembershipModal;
